import React, { useCallback, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { cn } from 'helpers/classnames';
import useMartyContext from 'hooks/useMartyContext';
import ProductUtils from 'helpers/ProductUtils';
import type { ProductSizing, ProductStyle } from 'types/cloudCatalog';
import type { FormattedProductBundle } from 'reducers/detail/productDetail';
import type { SelectedSizing } from 'types/product';
import AddToCartSticky from 'components/productdetail/stylepicker/AddToCartSticky';
import { ADD_TO_CART_BUTTON_ID } from 'constants/addToCart';
import { StockMessage } from 'components/productdetail/StockMessage/StockMessage';
import { useIsEnrolledInVariant } from 'hooks/unleash/useIsEnrolledInVariant';
import { FEATURE_PDP_OOS_UX } from 'constants/features';
import { OutOfStock } from 'components/productdetail/OutOfStock/OutOfStock';
import { track } from 'apis/amethyst';
import { evProductNotifyMe } from 'events/favorites';
import { useStockNotifications } from 'hooks/useStockNotifications';

import css from 'styles/components/productdetail/addToCart.scss';

interface Props {
  addToCartText: string;
  productDetail?: FormattedProductBundle;
  className?: string;
  useStickyAddToCart: boolean;
  isGiftCard: boolean;
  lowStockMessage: string;
  onAddToCart: (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>, isSticky: boolean, isStickyBelowFold: boolean) => void;
  onHideSelectSizeTooltip: () => void;
  onShowSelectSizeTooltip: () => void;
  productStyle: ProductStyle;
  selectedSizing: SelectedSizing;
  sizing: ProductSizing;
  form?: string;
  shouldExcludeNotifyMe?: boolean;
}

export function formatStockMessage(message: string) {
  const pattern = /^Only (\d+) in stock$/;
  const match = message.match(pattern);
  return match ? `Only ${match[1]} left in stock!` : message;
}

export const AddToCart = (props: Props) => {
  const {
    addToCartText,
    productDetail,
    className,
    useStickyAddToCart,
    isGiftCard,
    lowStockMessage,
    onAddToCart,
    onHideSelectSizeTooltip,
    onShowSelectSizeTooltip,
    productStyle,
    productStyle: { colorId, productId },
    selectedSizing,
    sizing,
    form,
    shouldExcludeNotifyMe
  } = props;
  const {
    marketplace: {
      pdp: { oosDisableButton }
    },
    preventOnTouchDevice,
    testId
  } = useMartyContext();
  const { isEnrolled: isPdpOosUx } = useIsEnrolledInVariant(FEATURE_PDP_OOS_UX);

  // Create a ref for the InView observer
  const { ref: inViewRef, inView, entry } = useInView({ threshold: 0 });

  // Create a ref to store the current element
  const elementRef = useRef<HTMLElement | null>(null);

  // Create a callback ref that updates both refs
  const setRef = useCallback(
    (element: HTMLElement | null) => {
      // Update our internal ref
      elementRef.current = element;

      // Update the InView ref
      inViewRef(element);
    },
    [inViewRef]
  );

  const signupForStockNotifications = useStockNotifications();

  let onHand = 1;
  let buttonText = addToCartText;
  let stockInput: JSX.Element | undefined;
  let stockId: string | undefined;
  let isDisabled: boolean | undefined;
  const stock = ProductUtils.getStockBySize(sizing.stockData, colorId, selectedSizing);
  const oosButtonText = 'Out of Stock';
  const shouldShowStockMessage = ProductUtils.shouldShowStockMessage({ lowStockMessage, sizing, colorId, selectedSizing, isPdpOosUx });
  const selectedOptions = ProductUtils.formatSelectedOptions(sizing, productStyle, selectedSizing);

  // If all dimensions are chosen, check the stock
  const isSelectionValid = ProductUtils.isSizeSelectionComplete(sizing, selectedSizing);
  if (!stock) {
    isDisabled = oosDisableButton;
    onHand = 0;
    buttonText = oosButtonText;
  }

  if (isSelectionValid) {
    if (stock) {
      stockInput = <input type="hidden" name="stockId" value={stock.id} />;
      stockId = stock.id;
      onHand = parseInt(stock.onHand);
    } else {
      isDisabled = oosDisableButton;
      onHand = 0;
      buttonText = oosButtonText;
    }
  }

  const shouldShowOOSExperience = isPdpOosUx && !shouldExcludeNotifyMe && !stock;

  return (
    <div className={cn(css.addToCartControl, { [css.giftCard]: isGiftCard }, className)}>
      {stockInput}
      <input type="hidden" name="productId" value={productId} />
      <input type="hidden" name="colorId" value={colorId} />
      {(!isPdpOosUx || shouldExcludeNotifyMe) && shouldShowStockMessage && (
        <div className={cn({ ['mb-3']: shouldExcludeNotifyMe })}>
          <StockMessage onHand={onHand} />
        </div>
      )}
      <div className={css.btnContainer}>
        {shouldShowOOSExperience ? (
          <OutOfStock.Root
            colorId={colorId}
            selectedSizing={selectedSizing}
            productId={productId}
            onSubmit={async ({ email, stockId, newsletter }) => {
              await signupForStockNotifications(email, stockId, newsletter);

              track(() => [
                evProductNotifyMe,
                {
                  productId,
                  colorId,
                  shamelessPlug: newsletter,
                  newStyles: false
                }
              ]);
            }}
          >
            <OutOfStock.Trigger disabled={isDisabled || !isSelectionValid}>Notify Me</OutOfStock.Trigger>

            <OutOfStock.Drawer title="Get Back-in-Stock Alerts">
              <OutOfStock.Form selectedOptions={selectedOptions} productName={productDetail?.brandProductName} productStyle={productStyle} />
            </OutOfStock.Drawer>
          </OutOfStock.Root>
        ) : (
          <button
            type="submit"
            id={ADD_TO_CART_BUTTON_ID}
            className={css.cartButton}
            onMouseEnter={preventOnTouchDevice(onShowSelectSizeTooltip)}
            onMouseLeave={preventOnTouchDevice(onHideSelectSizeTooltip)}
            onFocus={preventOnTouchDevice(onShowSelectSizeTooltip)}
            onBlur={preventOnTouchDevice(onHideSelectSizeTooltip)}
            disabled={!stock || isDisabled}
            data-stock-id={stockId}
            data-track-action="Product-Page"
            data-track-label="PrForm"
            data-track-value="Add-To-Cart"
            data-test-id={testId('addToCart')}
            ref={setRef}
            form={form}
          >
            {buttonText}
          </button>
        )}
      </div>
      {useStickyAddToCart && (
        <AddToCartSticky
          inView={inView}
          entry={entry}
          productDetail={productDetail}
          addToCartText={buttonText}
          isDisabled={onHand < 1}
          stockId={stockId}
          productStyle={productStyle}
          isSelectionValid={isSelectionValid}
          onAddToCart={onAddToCart}
          onShowSelectSizeTooltip={onShowSelectSizeTooltip}
          onHideSelectSizeTooltip={onHideSelectSizeTooltip}
        />
      )}
    </div>
  );
};

export default AddToCart;
